import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useServiceCategories } from '../../services/serviceCategoryService';
import MarketingReveal from './MarketingReveal';

/**
 * Day-polarity category strip — cream canvas, thin title, quiet chips / icon tiles.
 * Keeps existing category browse; adds icon tiles + rail fades when available.
 */
const ServiceCategories = () => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const { data: categoriesData, isLoading } = useServiceCategories({
    active: true,
    sort_by: 'sort_order',
    sort_order: 'asc',
    per_page: 20,
  });

  const categories = categoriesData?.data?.data || [];
  const hasIcons = categories.some((c) => c.icon_url);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      ro.disconnect();
    };
  }, [categories.length]);

  const scrollBy = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 280, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <section className="bg-m-canvas-light py-24 sm:py-28">
        <div className="page-container">
          <h2 className="marketing-section-title mb-4 text-m-ink">
            Everything you need for your event
          </h2>
          <p className="marketing-body text-m-ink-muted">Loading categories…</p>
        </div>
      </section>
    );
  }

  if (!categories.length) return null;

  return (
    <section className="border-b border-m-border-light bg-m-canvas-light py-24 sm:py-28">
      <MarketingReveal className="page-container">
        <div className="mb-14 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <h2 className="marketing-section-title mb-4 text-m-ink">
              Everything you need for your event
            </h2>
            <p className="marketing-body-lg text-m-ink-muted">
              Pick a category, or open the full photo-led vendor list.
            </p>
          </div>
          <Link
            to="/browse-vendors"
            className="group inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-m-ink transition-opacity hover:opacity-70"
          >
            Browse all vendors
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </div>

        <div className="marketing-rail marketing-rail-cream relative">
          {canScrollLeft ? (
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="absolute -left-1 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-m-border-light bg-m-surface text-m-ink shadow-sm transition-colors hover:border-m-ink/30 sm:flex"
              aria-label="Scroll categories left"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          ) : null}
          {canScrollRight ? (
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="absolute -right-1 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-m-border-light bg-m-surface text-m-ink shadow-sm transition-colors hover:border-m-ink/30 sm:flex"
              aria-label="Scroll categories right"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : null}

          <div
            ref={scrollRef}
            className={`flex overflow-x-auto pb-1 scrollbar-hide ${
              hasIcons ? 'gap-4' : 'gap-3'
            }`}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {categories.map((category) => {
              const href = `/category/${category.id}?title=${encodeURIComponent(
                category.category_name || 'Services'
              )}`;

              if (hasIcons) {
                return (
                  <Link
                    key={category.id}
                    to={href}
                    className="group flex w-[7.5rem] shrink-0 flex-col sm:w-32"
                  >
                    <div className="mb-3 aspect-square overflow-hidden rounded-2xl border border-m-border-light bg-m-surface transition-all duration-300 group-hover:border-m-ink/40 group-hover:shadow-[0_12px_28px_rgba(0,0,0,0.08)]">
                      {category.icon_url ? (
                        <img
                          src={category.icon_url}
                          alt=""
                          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-m-ink text-lg font-medium text-white">
                          {(category.category_name || '?').charAt(0)}
                        </div>
                      )}
                    </div>
                    <span className="line-clamp-2 text-center text-sm font-medium tracking-tight text-m-ink">
                      {category.category_name}
                    </span>
                  </Link>
                );
              }

              return (
                <Link key={category.id} to={href} className="group shrink-0">
                  <span className="inline-flex items-center rounded-full border border-m-border-light bg-m-surface px-5 py-3 text-sm font-medium tracking-tight text-m-ink transition-all duration-300 group-hover:border-m-ink group-hover:bg-m-ink group-hover:text-white group-hover:shadow-[0_8px_20px_rgba(0,0,0,0.12)]">
                    {category.category_name}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </MarketingReveal>
    </section>
  );
};

export default ServiceCategories;
